import type { ReactNode } from 'react';

interface DragGhostPlaceholderProps {
  /** Layout direction — horizontal bar for vertical lists,
   *  vertical bar for horizontal (flex-row) containers. */
  orientation?: 'horizontal' | 'vertical';
  /** Optional richer preview to display inside the placeholder region.
   *  When provided, the default insertion bar is replaced by this content. */
  renderPreview?: () => ReactNode;
}

/**
 * Visual placeholder rendered at the projected drop position while dragging.
 *
 * By default shows a slim coloured insertion bar. Pass `renderPreview` to
 * swap in a richer block preview later without changing consuming code.
 */
export function DragGhostPlaceholder({
  orientation = 'horizontal',
  renderPreview,
}: DragGhostPlaceholderProps) {
  if (renderPreview) {
    return (
      <div className="lw-drag-ghost pointer-events-none">
        {renderPreview()}
      </div>
    );
  }

  if (orientation === 'vertical') {
    // Vertical bar — used inside section flex rows between columns
    return (
      <div
        className="lw-drag-ghost pointer-events-none flex items-stretch self-stretch py-1"
        aria-hidden
      >
        <div className="w-1 rounded-full bg-primary/60 animate-in fade-in zoom-in-95 duration-150" />
      </div>
    );
  }

  // Horizontal bar — used between vertically stacked blocks
  return (
    <div
      className="lw-drag-ghost pointer-events-none px-2 py-1"
      aria-hidden
    >
      <div className="h-1 rounded-full bg-primary/60 animate-in fade-in zoom-in-95 duration-150" />
    </div>
  );
}
